import React from 'react';
import { FaEdit, FaTrash, FaArrowLeft, FaFilePdf, FaFileImage, FaFileAlt, FaFileWord, FaFileExcel } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const TaskView = ({ task, onClose, onEdit, onDelete }) => {
    const navigate = useNavigate();

    const getDocumentIcon = (doc) => {
        if (!doc) return <FaFileAlt className="text-gray-400" />;

        const extension = doc.fileName?.split('.').pop().toLowerCase();
        switch (extension) {
            case 'pdf':
                return <FaFilePdf className="text-red-500" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'svg':
                return <FaFileImage className="text-blue-400" />;
            case 'doc':
            case 'docx':
                return <FaFileWord className="text-blue-600" />;
            case 'xls':
            case 'xlsx':
                return <FaFileExcel className="text-green-600" />;
            default:
                return <FaFileAlt className="text-gray-400" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed':
                return 'bg-green-100 text-green-800';
            case 'In Progress':
                return 'bg-blue-100 text-blue-800';
            case 'Not Started':
                return 'bg-gray-100 text-gray-800';
            case 'Deferred':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const isOverdue = (dueDate, status) => {
        if (!dueDate) return false;
        return new Date(dueDate) < new Date() && !['Completed', 'Deferred'].includes(status);
    };

    if (!task) return null;

    return (
        <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl mx-auto">
            <div className="flex justify-between items-start mb-4">
                <button
                    onClick={onClose}
                    className="flex items-center text-gray-600 hover:text-gray-800"
                >
                    <FaArrowLeft className="mr-2" />
                    Back to tasks
                </button>
                <div className="flex space-x-2">
                    <button
                        onClick={() => onEdit(task.id)}
                        className="flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                        <FaEdit className="mr-2" />
                        Edit
                    </button>
                    <button
                        onClick={() => onDelete(task.id)}
                        className="flex items-center px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                        <FaTrash className="mr-2" />
                        Delete
                    </button>
                </div>
            </div>

            <div className="mb-6">
                <div className="flex justify-between items-start">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{task.title}</h1>
                    {task.reminder && (
                        <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Reminder
                        </span>
                    )}
                </div>

                <div className="flex items-center space-x-4 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
                        {task.status}
                    </span>
                    {task.dueDate && (
                        <span className={`text-sm ${isOverdue(task.dueDate, task.status) ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                            {isOverdue(task.dueDate, task.status) && ' (Overdue)'}
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Description</h2>
                    <p className="text-gray-600 whitespace-pre-line">
                        {task.description || 'No description provided'}
                    </p>
                </div>

                <div>
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-gray-700 mb-2">Assigned To</h2>
                        <p className="text-gray-600">{task.assignedTo || 'Unassigned'}</p>
                    </div>

                    {task.attachments && task.attachments.length > 0 && (
                        <div>
                            <h2 className="text-lg font-semibold text-gray-700 mb-2">Attachments</h2>
                            <div className="space-y-2">
                                {task.attachments.map((doc, index) => (
                                    <div key={index} className="flex items-center text-blue-600 hover:text-blue-800">
                                        {getDocumentIcon(doc)}
                                        <span className="ml-2">{doc.fileName}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskView;